import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import type { action as projectAction } from '~/routes/remix-api.directory.set-project-in-library';
import type { action as productAction } from '~/routes/remix-api.directory.set-product-in-library';
import { SET_PROJECT_IN_LIBRARY } from '~/routes/remix-api.directory.set-project-in-library';
import { SET_PRODUCT_IN_LIBRARY } from '~/routes/remix-api.directory.set-product-in-library';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useAppData } from '~/modules/root/hook/use-app-data';

export type LibraryItemType = 'project' | 'product';

type ErrorCallbackFn = (error: { __typename: string; Message: string }) => void;
type SuccessCallbackFn = (
    itemId: number,
    itemType: LibraryItemType,
    inLibrary: boolean,
) => void;

/**
 * Hook to toggle the InLibrary flag on a Project or Product.
 * Setting InLibrary=false soft-hides the item from the directory without
 * deleting it. The function is `undefined` for non-admin users.
 */
export function useSetInLibrary(
    onError?: ErrorCallbackFn,
    onSuccess?: SuccessCallbackFn,
) {
    const projectFetcher = useFetcher<typeof projectAction>();
    const productFetcher = useFetcher<typeof productAction>();
    const onErrorRef = useLatest<ErrorCallbackFn | undefined>(onError);
    const onSuccessRef = useLatest<SuccessCallbackFn | undefined>(onSuccess);
    const { user } = useAppData();

    const isStaffAdmin = user.__typename === 'Me' && user.IsStaffAdmin === true;
    const isHighAdmin =
        user.__typename === 'Me' &&
        'IsHighAdmin' in user &&
        user.IsHighAdmin === true;
    const canModify = isStaffAdmin || isHighAdmin;

    // Separate refs per item type so concurrent project + product submissions
    // can't clobber each other's submitted snapshot.
    type SubmittedSnapshot = { itemId: number; inLibrary: boolean };
    const lastProjectSubmittedRef = useRef<SubmittedSnapshot | null>(null);
    const lastProductSubmittedRef = useRef<SubmittedSnapshot | null>(null);

    const setInLibrary = useCallback(
        (itemType: LibraryItemType, itemId: number, inLibrary: boolean) => {
            if (itemId <= 0) {
                return;
            }
            if (itemType === 'project') {
                lastProjectSubmittedRef.current = { itemId, inLibrary };
                projectFetcher.submit(
                    {
                        projectID: itemId,
                        inLibrary: inLibrary,
                    },
                    {
                        method: 'post',
                        action: SET_PROJECT_IN_LIBRARY,
                    },
                );
            } else {
                lastProductSubmittedRef.current = { itemId, inLibrary };
                productFetcher.submit(
                    {
                        productID: itemId,
                        inLibrary: inLibrary,
                    },
                    {
                        method: 'post',
                        action: SET_PRODUCT_IN_LIBRARY,
                    },
                );
            }
        },
        [projectFetcher, productFetcher],
    );

    useEffect(() => {
        if (!fetcherIsDone(projectFetcher)) {
            return;
        }
        const result = projectFetcher.data?.SetProjectInLibrary;
        const submitted = lastProjectSubmittedRef.current;
        if (
            result?.__typename === 'UserError' ||
            result?.__typename === 'NotAuthorizedError'
        ) {
            onErrorRef.current?.(result);
        } else if (result?.__typename === 'Project' && submitted) {
            onSuccessRef.current?.(
                submitted.itemId,
                'project',
                submitted.inLibrary,
            );
        }
    }, [projectFetcher, onErrorRef, onSuccessRef]);

    useEffect(() => {
        if (!fetcherIsDone(productFetcher)) {
            return;
        }
        const result = productFetcher.data?.SetProductInLibrary;
        const submitted = lastProductSubmittedRef.current;
        if (
            result?.__typename === 'UserError' ||
            result?.__typename === 'NotAuthorizedError'
        ) {
            onErrorRef.current?.(result);
        } else if (result?.__typename === 'Product' && submitted) {
            onSuccessRef.current?.(
                submitted.itemId,
                'product',
                submitted.inLibrary,
            );
        }
    }, [productFetcher, onErrorRef, onSuccessRef]);

    return {
        setInLibrary: canModify ? setInLibrary : undefined,
    };
}
